import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "@commons/Modal";
import TextInput from "@commons/Input/Text";
import { usePage } from "@hooks/usePage";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import { globalMutate } from "@utils/swr";
import { VisitTypeHelper } from "@typings/entities/visit/enums";
import { SAMPLE_TYPE_OPTIONS } from "@typings/entities/samples/enums";
import RadioInput from "@commons/Input/Radio";
import MultiSelectInput from "@commons/Input/MultiSelect";
import {
  visitDefaultValues,
  visitSchema,
  type VisitFormData,
} from "@modules/community/schemas/visitSchema";
import { updateVisit } from "@services/visits/update";
import { useGetVisitById } from "@services/visits/getById";
import { useEffect } from "react";
import { formatDateForUpdate } from "@utils/formatters";

function ModalEditVisit() {
  const { isModalOpen, closeModal, setIsLoading, setDataModal, dataModal } =
    usePage();
  const open = isModalOpen("editVisit");

  const visitId = dataModal?.id as string;
  const { data, isLoading, error } = useGetVisitById(visitId);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<VisitFormData>({
    resolver: zodResolver(visitSchema),
    defaultValues: visitDefaultValues,
  });

  useEffect(() => {
    if (open && data) {
      const visit = data.visit;

      setValue("date", formatDateForUpdate(visit.date));
      setValue("responsibleTeam", visit.responsibleTeam);
      setValue("visitType", visit.visitType || undefined);
      setValue("sampleTypes", visit.sampleTypes || []);
    }
  }, [open, data, setValue]);

  const onSubmit = async (visitForm: VisitFormData) => {
    try {
      setIsLoading(true);

      await updateVisit(visitId, {
        visitType: visitForm.visitType,
        date: visitForm.date,
        responsibleTeam: visitForm.responsibleTeam,
        sampleTypes: visitForm.sampleTypes,
      });

      toast.success("Visita atualizada com sucesso");

      await globalMutate(["fetch-get-visits", `fetch-visit-${visitId}`]);
      handleClose();
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError ? error.message : "Erro ao atualizar visita";

      toast.error(title);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setDataModal({});
    closeModal();
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      heading="Atualizar Visita"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      className="max-w-5xl relative"
      isLoading={isLoading}
      error={!!error}
    >
      <form className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <Controller
          name="date"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Data da Visita"
              textSize="sm"
              type="date"
              fullWidth
              error={!!errors.date}
              helperText={errors.date?.message}
            />
          )}
        />
        <div />

        <div>
          <Controller
            name="responsibleTeam"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Equipe Responsável"
                textSize="sm"
                fullWidth
                error={!!errors.responsibleTeam}
                helperText={errors.responsibleTeam?.message}
              />
            )}
          />
        </div>

        <div className="flex items-end">
          <Controller
            name="visitType"
            control={control}
            render={({ field }) => (
              <RadioInput
                {...field}
                value={field.value || ""}
                onChange={(e) => {
                  field.onChange(e.target.value === "" ? null : e.target.value);
                }}
                error={!!errors.visitType}
                helperText={errors.visitType?.message}
                options={VisitTypeHelper.getOptions()}
              />
            )}
          />
        </div>

        <div className="md:col-span-2">
          <Controller
            name="sampleTypes"
            control={control}
            render={({ field }) => (
              <MultiSelectInput
                {...field}
                label="Amostras Coletadas"
                value={field.value ?? []}
                options={SAMPLE_TYPE_OPTIONS}
              />
            )}
          />
        </div>
      </form>
    </Modal>
  );
}

export default ModalEditVisit;
